import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { DEFAULT_SITE_REF_ID } from '../../constants'

import { ReorderableCategories } from './reorderable-categories'
import { CategoryCreate } from './category-create'
import { CategoryUpdate } from './category-update'
import { CategoryDelete } from './category-delete'
import { CategoryUpdateTours } from './category-update-tours'
import { getSubnav } from './subnav'

import { Button } from 'components/ui/button'
import { SelectFilter, SelectFilterItem } from 'components/ui/select-filter'
import { FooterEditEntity } from 'components/ui/footer'
import { IconPlus } from 'components/ui/icons'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { StatusGuard } from 'components/ui/status-guard'
import { Confirm } from 'components/ui/modal'
import { StatusSummary } from 'components/ui/status-summary'

import { GettyStore, GettyAppState } from 'domains/org/getty/store'
import { getSearchFilterValues, saveSearchFilterValue } from 'domains/org/getty/utils/search-filter'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { IssueMesages } from 'domains/org/getty/utils/validation'

import { Page } from 'generated/mos/pagination'
import { Site } from 'generated/mos/structure'
import { TranslatableStringValue } from 'generated/mos/translatable'
import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { TourCategory, Tour } from 'generated/mos/org/getty/audio'
import { UpdateTourCategoryRequest, UpdateTourCategoriesRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { areObjectsEqual, pick, pluralise, isNotNullOrUndefined } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'

const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const NoResults = styled.div`
  text-align: center;
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
`;

type ActionProps =
  Pick<GettyActionCreators, 'gettyListSitesRequest' | 'gettyListTourCategoriesRequest' | 'gettyUpdateTourCategoriesRequest' | 'gettyListValidationFailuresRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<SharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyAppState, | 'sites' | 'categories' | 'categoryDelete' | 'categoryCreate' | 'validationFailuresList'>

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  navBackRoute?: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

const sortInitialCategories = (categories: ReadonlyArray<TourCategory.Entity>): ReadonlyArray<TourCategory.Entity> => {
  const arr = categories as Array<TourCategory.Entity>
  return arr.sort((a, b) => a.siteOrder - b.siteOrder)
}

const CategoriesPageView = (props: Props) => {

  const {
    projectId,
    gettyListTourCategoriesRequest,
    gettyListSitesRequest,
    toastNotification,
    categoryDelete,
    categoryCreate,
    sites,
    categories,
    historyPush,
    gettyUpdateTourCategoriesRequest,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
    gettyListValidationFailuresRequest,
    validationFailuresList,
  } = props;

  const categoriesData = statusSelector.data(categories)
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const hasSubmitted = useRef(false)

  const [ showCategoryCreate, setShowCategoryCreate ] = useState<boolean>(false)
  const [ siteWarning, setSiteWarning ] = useState<Site.Ref| undefined>(undefined)
  const [ deleteCategory, setDeleteCategory ] = useState<TourCategory.Entity | undefined>(undefined)
  const [ updateCategory, setUpdateCategory ] = useState<TourCategory.Entity | undefined>(undefined)
  const [ updateCategoryTours, setUpdateCategoryTours ] = useState<TourCategory.Entity | undefined>(undefined)
  const [ orderedCategories, setOrderedCategories ] = useState<ReadonlyArray<TourCategory.Entity> | undefined>(categoriesData && categoriesData.tourCategories);
  const [ initialOrder, setInitialOrder] = useState<ReadonlyArray<TourCategory.Entity> | undefined>(undefined)

  const filterValues = sessionStorage.length && getSearchFilterValues()
  const sessionSiteId = filterValues.categoriesLocationRef ? filterValues.categoriesLocationRef.id : DEFAULT_SITE_REF_ID

  const selectedSite =
    sites.status === Status.Ready &&
    sites.data.sites &&
    sites.data.sites.filter(site => Site.mustRef(site.ref).id === sessionSiteId)[0]

  const defaultSiteRef = {
    typename: Site.refName,
    id: DEFAULT_SITE_REF_ID
  }

  useEffect(() => {
    gettyListSitesRequest()
  }, [gettyListSitesRequest])

  useEffect(() => {
    if(selectedSite && selectedSite.ref && !modified) {
      gettyListTourCategoriesRequest(selectedSite.ref)
    }
  }, [sites.status, sites, selectedSite]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (categories.status === Status.Ready) {
      setInitialOrder(sortInitialCategories(categories.data.tourCategories))
      setOrderedCategories(sortInitialCategories(categories.data.tourCategories))

      const refs = categories.data.tourCategories.map(({ ref }) => ref).filter(isNotNullOrUndefined)
      releaseControlsListReleaseControlsRequest(refs)

      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: refs
        }
      })
    }

    if (categories.status === Status.Ready && hasSubmitted.current) {
      const refs = categories.data.tourCategories.map(({ ref }) => ref).filter(isNotNullOrUndefined)
      releaseControlsListReleaseControlsRequest(refs)
      toastNotification({ type: 'success', text: 'Categories updated' })
      hasSubmitted.current = false
    }
  }, [categories.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updateTourCategories = (tourCategories: ReadonlyArray<TourCategory.Entity>) => {
      setOrderedCategories(orderedCategories && orderedCategories.filter(orderedCategory => tourCategories.filter(tourCategory => tourCategory.ref === orderedCategory.ref).length))
      setInitialOrder(initialOrder && initialOrder.filter(initialOrderCategory => tourCategories.filter(tourCategory => tourCategory.ref === initialOrderCategory.ref).length))
    }

    if (categories.status === Status.Ready && categoryDelete.status === Status.Complete) {
      updateTourCategories(categories.data.tourCategories)
    }
  }, [categoryDelete.status, categories, orderedCategories, initialOrder])

  useEffect(() => {
    const updateTourCategories = (tourCategories: ReadonlyArray<TourCategory.Entity>) => {
      if(orderedCategories && initialOrder) {
        const newCategory: Array<TourCategory.Entity> = tourCategories.filter(tourCategory => !orderedCategories.filter(orderedCategory => orderedCategory.ref === tourCategory.ref).length)
        const updatedCategories: ReadonlyArray<TourCategory.Entity> = [...orderedCategories, ...newCategory]
        const updatedInitialCategories: ReadonlyArray<TourCategory.Entity> = [...initialOrder, ...newCategory]
        setOrderedCategories(updatedCategories)
        setInitialOrder(updatedInitialCategories)
      }
    }
    if (categories.status === Status.Ready && categoryCreate.status === Status.Complete) {
      updateTourCategories(categories.data.tourCategories)
    }
  }, [categoryCreate.status, categories, orderedCategories, initialOrder])

  useEffect(() => {
    categoryDelete.status === Status.Complete &&
      toastNotification({
        type: 'success',
        text: 'Category deleted successfully',
      })
  }, [categoryDelete.status, toastNotification])

  const siteOpts = sites.status === Status.Ready && sites.data.sites
      ? sites.data.sites.map((site: Site.Entity) => ({
        label: site.name,
        value: site,
      }))
      : []

  const saveHandler = () => {
    if(!orderedCategories) {
      toastNotification({
        type: 'error',
        text: 'Categories could not be updated',
      })
    } else {
      const tourCategoryUpdates = orderedCategories.map(
        ({ ref, tourRefs, name }: TourCategory.Entity, index): UpdateTourCategoryRequest.Entity => ({
          ...UpdateTourCategoryRequest.defaults,
          tourCategoryRef: ref,
          updateMask: { ...UpdateTourCategoryRequest.defaults.updateMask, fields: ['tourRefs', 'siteOrder', 'name'] },
          tourRefs,
          name,
          siteOrder: index
        })
      )

      hasSubmitted.current = true

      orderedCategories && gettyUpdateTourCategoriesRequest({
        ...UpdateTourCategoriesRequest.defaults,
        tourCategoryUpdates,
      })
    }
  }

  const deleteHandler = (category: TourCategory.Entity) => setDeleteCategory(category)
  const editHandler = (category: TourCategory.Entity) => setUpdateCategory(category)
  const updateToursHandler = (category: TourCategory.Entity) => setUpdateCategoryTours(category)
  const storeTourUpdates = (categoryRef: TourCategory.Ref, tourRefs: ReadonlyArray<Tour.Ref>) => {
    const updatedCategories: ReadonlyArray<TourCategory.Entity> | undefined = orderedCategories && orderedCategories.map(category => {
      if(category.ref === categoryRef) {
        return {
          ...category,
          tourRefs,
        }
      }
      return category
    })
    setOrderedCategories(updatedCategories)
  }

  const storeCategoryUpdate = (categoryRef: TourCategory.Ref, name: TranslatableStringValue.Entity) => {
    const updatedCategories: ReadonlyArray<TourCategory.Entity> | undefined = orderedCategories && orderedCategories.map(category => {
      if(category.ref === categoryRef) {
        return {
          ...category,
          name,
        }
      }
      return category
    })
    setOrderedCategories(updatedCategories)
  }

  const backLocation = `/project/${projectId}/tours`
  const modified = !!initialOrder && !!orderedCategories && !areObjectsEqual(initialOrder, orderedCategories)

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>, ref: TourCategory.Ref) => {
    releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.typename === TourCategory.refName) : undefined
  const failureFields = failures && failures.issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'TOUR_CATEGORY_CONTAINS_TOURS_FROM_MULTIPLE_SITES')
  const failureMessage = failureFields && IssueMesages[failureFields.issueType]

  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Manage Categories"
          onConfirm={modified ? () => historyPush(backLocation) : undefined}
          navBackRoute={backLocation}
        />
      )}
      title="Categories"
      subnav={() => (
        <SecondaryNavigation navItems={getSubnav(projectId)} modified={modified} historyPush={historyPush}>
          <Button variant="subtle" onClick={() => setShowCategoryCreate(true)}>
            <IconPlus size="small" />create category
          </Button>
        </SecondaryNavigation>
      )}
      summary={failureMessage ? () => failureMessage && <StatusSummary warning={[{ text: failureMessage }]} /> : undefined}

      content={() => (
        <>
          {showCategoryCreate && selectedSite &&
            <CategoryCreate onClose={() => { setShowCategoryCreate(false) }} siteRef={Site.mustRef(selectedSite && selectedSite.ref && selectedSite.ref)} />
          }
          {deleteCategory &&
            <CategoryDelete onCancel={() => setDeleteCategory(undefined)} category={deleteCategory} />
          }
          {updateCategory &&
            <CategoryUpdate onClose={() => setUpdateCategory(undefined)} category={updateCategory} onSubmit={storeCategoryUpdate} />
          }
          {updateCategoryTours &&
            <CategoryUpdateTours onClose={() => setUpdateCategoryTours(undefined)} category={updateCategoryTours} onSubmit={storeTourUpdates} />
          }
          {siteWarning &&
            <Confirm
              onClose={() => setSiteWarning(undefined) }
              title="Unsaved Changes"
              message="Do you want to discard your changes?"
              confirmLabel="Yes, discard"
              closeLabel="Cancel"
              onConfirm={() => {
                saveSearchFilterValue( {categoriesLocationRef: siteWarning} )
                gettyListTourCategoriesRequest(siteWarning)
                setSiteWarning(undefined)
              }}
            />
          }
          <FilterButtonRow>
            {sites &&
              <SelectFilter value={selectedSite ? selectedSite.name: ''}>
                {siteOpts.map(site => (
                  <SelectFilterItem key={site.label} selected={selectedSite && selectedSite.name === site.label} onClick={() => {
                    const updatedSite = site.value.ref ? site.value.ref : defaultSiteRef
                    if(modified) {
                      setSiteWarning(updatedSite)
                    } else {
                      saveSearchFilterValue( {categoriesLocationRef: updatedSite} )
                      gettyListTourCategoriesRequest(updatedSite)
                    }
                  }}>
                    {site.label}
                  </SelectFilterItem>
                ))}
              </SelectFilter>
            }
          </FilterButtonRow>

          <StatusGuard status={categories}>
            {orderedCategories && categoriesData && orderedCategories.length ?
              <>
                <SectionHeader heading={`${orderedCategories.length} ${pluralise(orderedCategories.length, "category", "categories")}`} />
                <ReorderableCategories
                  validationFailures={validationFailuresListData}
                  categories={categoriesData}
                  orderedCategories={orderedCategories}
                  setOrderedCategories={setOrderedCategories}
                  projectId={projectId}
                  historyPush={historyPush}
                  deleteHandler={deleteHandler}
                  editHandler={editHandler}
                  updateToursHandler={updateToursHandler}
                  releaseControlUpdate={releaseControlUpdate}
                />
              </>
              : <NoResults>
                <h3>No Categories</h3>
                <p>This site has no categories. Add or create some categories.</p>
              </NoResults>
            }
          </StatusGuard>
        </>
      )}
      footer={() => categories && (
        <FooterEditEntity
          entity={categories}
          modified={modified}
          onCancel={() => historyPush(backLocation)}
          onSave={saveHandler}
        />
      )}
    />
  )
}

export const CategoriesPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'sites', 'categories','categoryDelete', 'categoryCreate', 'validationFailuresList'),
  }),
  {
    ...pick(actionCreators, 'gettyListTourCategoriesRequest', 'gettyListSitesRequest', 'gettyDeleteTourCategoryRequest', 'gettyUpdateTourCategoriesRequest', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(CategoriesPageView);
